import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import * as Sentry from "@sentry/react";
import * as braze from "@braze/web-sdk";
import { Box, Card, Stack } from "@mui/material";
import theme from "theme";
import RightArrowIcon from "images/arrow-right-black.svg";
import GradientButton from "@tomocredit/common-fe/components/common/GradientButton";
import { usePopup } from "components/common/wrappers/withPopup";
import axios from "axios";
import SignupConfirmationModal from "common/SignupConfirmationModal";
import { isValidEmail, trackLead, useQueryParamsAsJson } from "@tomocredit/common-fe/utils";
import { ANALYTICS_EVENTS, LEAD_SOURCES } from "@tomocredit/common-fe/consts";
import { BreakpointContext } from "components/common";
import useSystemTheme from "@tomocredit/common-fe/utils/useSystemTheme";
import Email from "common/EmailV2";
import AutoplayCarousel from "common/AutoplayCarousel";
import TestimonialCard1 from "images/boost/boost_testimonial_card-1.webp";
import TestimonialCard2 from "images/boost/boost_testimonial_card-2.webp";
import TestimonialCard3 from "images/boost/boost_testimonial_card-3.webp";
import TestimonialCard4 from "images/boost/boost_testimonial_card-4.webp";
import TestimonialCard1Light from "images/boost/boost_testimonial_card-1_light.png";
import TestimonialCard2Light from "images/boost/boost_testimonial_card-2_light.png";
import TestimonialCard3Light from "images/boost/boost_testimonial_card-3_light.png";
import TestimonialCard4Light from "images/boost/boost_testimonial_card-4_light.png";
import Guard3 from "images/Guard3.svg";
import DesktopImage1 from "images/boost/boost_landing_img1_desktop.png";
import DesktopImage2 from "images/boost/boost_landing_img2_desktop.png";
import DesktopImage3 from "images/boost/boost_landing_img3_desktop.png";
import DesktopImage4 from "images/boost/boost_landing_img4_desktop.png";
import DesktopImage5 from "images/boost/boost_landing_img5_desktop.png";
import DesktopImage1Light from "images/boost/boost_landing_img1_desktop_light.png";
import DesktopImage2Light from "images/boost/boost_landing_img2_desktop_light.png";
import DesktopImage3Light from "images/boost/boost_landing_img3_desktop_light.png";
import DesktopImage4Light from "images/boost/boost_landing_img4_desktop_light.png";
import DesktopImage5Light from "images/boost/boost_landing_img5_desktop_light.png";
import MobileImage1 from "images/boost/boost_landing_img1_mobile.png";
import MobileImage2 from "images/boost/boost_landing_img2_mobile.png";
import MobileImage3 from "images/boost/boost_landing_img3_mobile.png";
import MobileImage4 from "images/boost/boost_landing_img4_mobile.png";
import MobileImage5 from "images/boost/boost_landing_img5_mobile.png";
import MobileImage1Light from "images/boost/boost_landing_img1_mobile_light.png";
import MobileImage2Light from "images/boost/boost_landing_img2_mobile_light.png";
import MobileImage3Light from "images/boost/boost_landing_img3_mobile_light.png";
import MobileImage4Light from "images/boost/boost_landing_img4_mobile_light.png";
import MobileImage5Light from "images/boost/boost_landing_img5_mobile_light.png";
// import TransUnionLogo from "images/boost/logo-transunion.svg";
import ExperianLogo from "images/boost/logo-experian.svg";
import EquifaxLogo from "images/boost/logo-equifax.svg";
import ExperianLogoLight from "images/boost/logo-experian-light.svg";
import EquifaxLogoLight from "images/boost/logo-equifax-light.svg";
import { BoostBannerImage, CheckMark } from "@tomocredit/common-fe/components";
import { useQueryParams } from "utils/hooks/useQueryParams";
import { socialProofImages } from "./utils";

const BOOST_PRICING_URL = "/boost-pricing";

export const BureauLogo = ({ isMobile = false, lightTheme = false }) => {
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent={isMobile ? "space-evenly" : ""}
      alignItems={isMobile ? "center" : ""}
    >
      <img alt="equifax_logo" src={lightTheme ? EquifaxLogoLight : EquifaxLogo} style={{ height: "1.5rem", width: "6.5rem", marginTop: "1.5rem", opacity: "80%" }} />
      <img alt="experian_logo" src={lightTheme ? ExperianLogoLight : ExperianLogo} style={{ height: "2rem", width: "6.5rem", marginTop: "1rem", opacity: "80%" }} />
      {/* <img alt="trans_union_logo" src={TransUnionLogo} style={{ height: "1.5rem", width: "6.5rem", marginTop: isMobile ? "0.5rem" : "1rem", opacity: "80%" }} /> */}
    </Stack>
  );
};

const ActionCard = ({ title = "", subtitle = "", paragraphOne = "", paragraphTwo = "", imageClass = "", heroImage, heroImageMobile, ctaText = "", reversed = false, comingSoon = false, isMobile = false, lightTheme = false }) => {
  const [expanded, setExpanded] = useState(false);
  const truncatedText = paragraphOne.split(".")[0] || "";
  const history = useHistory();
  const withQueryParams = useQueryParams();

  const handleReadMore = () => {
    window.gtag?.("event", "LAND_BOOST_READ_MORE_CLICKED", {});
    setExpanded(true);
  };

  const handleReadLess = () => {
    window.gtag?.("event", "LAND_BOOST_READ_LESS_CLICKED", {});
    setExpanded(false);
  };

  return(
    <div className={`w-full pb-5 md:py-20 ${(lightTheme && !reversed) ? "bg-[#E3DBFD4D]" : "bg-transparent"}`}>
      <div
        data-testid="boost-landing-action-card"
        className={`flex flex-col-reverse ${reversed ? "md:flex-row-reverse" : "md:flex-row"} justify-between max-w-page m-4 md:m-auto`}
        onClick={(e) => {
          if(!e.target.classList.contains('ignore')) { // If clicked target is not 'read more' or 'read less'; do the redirect
            window.gtag?.("event", "LAND_BOOST_HIDDEN_CLICKED", {});
            history.push(withQueryParams(BOOST_PRICING_URL));
          }
        }}
      >
        <Card
          sx={(muiTheme) => ({
            paddingX: "2.5rem",
            paddingY: "3rem",
            borderRadius: "10px",
            // eslint-disable-next-line import/no-named-as-default-member
            background: lightTheme ? "#F5F5F5" : theme.colors["neutral-700"],
            color: 'white',
            flexGrow: 1,
            margin: "auto",
            maxWidth: "41rem",
            boxShadow: 2,
            [muiTheme.breakpoints.down("md")]: {
              padding: "2rem",
              marginX: "1rem",
            },
          })}
        >
          {title &&
            <span className={`text-3xl font-bold ${lightTheme ? "text-black" : "text-white"}`}>{/* Update to use theme color */}{title}<span className="text-boost-primary-500">.</span></span>
          }
          {subtitle &&
            <p className={`text-2xl font-bold ${lightTheme ? "text-black" : "text-white"}`}>{/* Update to use theme color */}{subtitle}</p>
          }
          {!expanded &&
            <span>
              {/* Update to use theme color in the future */}
              <p className={`mt-4 text-lg ${lightTheme ? "text-black" : "text-white"}`}>{comingSoon && <b>Coming Soon: </b>}{truncatedText}... <button type="button" className={`italic font-bold text-lg ${lightTheme ? "text-gray-800" : "text-info-500"} ignore`} onClick={handleReadMore}>read more</button></p>
            </span>
          }
          {expanded && paragraphOne &&
            <p className={`mt-4 text-lg ${lightTheme ? "text-black" : "text-white"}`}>{comingSoon && <b>Coming Soon: </b>}{paragraphOne}</p>
          }
          {expanded && paragraphTwo &&
            <p className={`mt-4 text-lg ${lightTheme ? "text-black" : "text-white"}`}>{paragraphTwo}</p>
          }
          {expanded &&
            <button type="button" className={`inline italic font-bold text-lg ${lightTheme ? "text-gray-800" : "text-info-500"}`} ignore onClick={handleReadLess}>read less</button>
          }
          {ctaText &&
            <GradientButton
              color="black" marginTop="2rem" className="ignore"
              clickMethod={() => {
                window.gtag?.("event", "LAND_BOOST_CTA_CLICKED", {});
                history.push(withQueryParams(BOOST_PRICING_URL));
              }}
              padding={0}
            >
              <span className="flex flex-row ignore">{ctaText} <img alt="" src={RightArrowIcon} /></span>
            </GradientButton>
          }
        </Card>
        <div className="p-6 mb-4 md:mb-0 grid place-items-center flex-grow">
          <img alt="" src={isMobile ? heroImageMobile : heroImage} className={imageClass} />
        </div>
      </div>
    </div>
  );
};

export const BoostLanding = () => {
  const { isBelow } = useContext(BreakpointContext);
  const belowMd = isBelow("md");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalEmail, setModalEmail] = useState("");
  const [modalState, setModalState] = useState("");
  // const { promoInfo } = useContext(PromoContext);
  const history = useHistory();
  const { hideExitPopup, hidePromoPopupRef } = usePopup();
  const { capturedUrlParams } = useQueryParamsAsJson(['email']);
  const withQueryParams = useQueryParams();
  const useLightTheme = useSystemTheme() === 'light'; 

  useEffect(() => {
    window.gtag?.("event", "Landing_Theme_Loaded", {
      theme: useLightTheme ? "Light" : "Dark",
      platform: navigator?.platform,
    });
  }, []);

  const closeModal = () => {
    setModalState("");
    setModalEmail("");
  };

  const handleSubmit = async (email) => {
    if (loading) {
      return;
    }
    setError(null);
    if (!email) {
      setError("Email address is required");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Invalid email address");
      return;
    }
    setLoading(true);

    let signUpBoostRes;
    try {
      const payload = { email, source: LEAD_SOURCES.BOOST_LANDING, capturedUrlParams };
      signUpBoostRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/boost/signup`,
        payload,
      );
    } catch (err) {
      setError("Could not communicate with server. Please try again later");
      return;
    } finally {
      setLoading(false);
    }

    const { userExists, loginExist, boostEnrolled, guid, softTerminated } = signUpBoostRes?.data ?? {};

    if (guid) {
      braze.changeUser(guid);
      braze.getUser()?.setEmail(email);
    }

    if (loginExist && boostEnrolled && !softTerminated) {
      hideExitPopup();
      window.location.assign(
        `${
          process.env.REACT_APP_DOMAIN
        }/dashboard/login?redirect_uri=/boost&email=${encodeURIComponent(
          email,
        )}&existingAccount=true&fromBoost=true`,
      );
      return;
    }
    if (!loginExist && boostEnrolled && !softTerminated) {
      setModalState("boostEnrolledWithNoLogin");
      return;
    }
    if (!boostEnrolled || softTerminated) {
      const redirectToPricing = () => {
        history.push(withQueryParams(BOOST_PRICING_URL, { email }));
      };

      trackLead({ email });
      if (userExists) {
        redirectToPricing();
      } else {
        window.gtag?.("event", ANALYTICS_EVENTS.NEW_LEAD, {
          email,
          source: LEAD_SOURCES.BOOST_LANDING,
        });
        window?.gtag?.("event", "boost_new_signup", {
          email,
          event_callback: redirectToPricing,
        });
        window.uetq?.push('event', 'lead',
          {
            event_label:"lead",
            event_category:"lead",
            contact: email,
          },
        );
        if (typeof window.gtag !== "function") {
          Sentry.captureException(new Error(`New signup detected in ${LEAD_SOURCES.BOOST_LANDING} but gtag is not loaded`));
        }
      }
      return;
    }

    // user not exist
    setError("Could not communicate with server. Please try again later");
  };

  return (
    <div
      className="flex flex-col md:pt-0 justify-center text-white leading-normal"
      style={{ paddingBottom: "30px", letterSpacing: 0.1 }}
    >
      {/* <BoostChatBotScript/> */}
      {/* BANNER SECTION */}
      <div
        className={belowMd ? "flex w-full justify-evenly items-center flex-col-reverse px-6 pt-16" : "flex w-full justify-evenly items-center xl:flex-row lg:flex-row md:flex-row flex-col-reverse px-6 md:px-10 mx-auto"}
        style={{ maxWidth: 1300 }}
      >
        <div className="md:w-1/2">
          <div className="pt-10 hidden md:block">
            <h1 className="mx-2 text-center md:text-left text-5xl xl:text-6xl xl:banner-title banner-title-sm font-semibold pw-7/12 md:w-full tracking-tight leading-none sm:-mt-10 md:mt-0">
              <span className={`${useLightTheme ? "text-black" : "text-white"}`}>Build Credit Fast</span>
              <span className="text-boost-primary-500">.</span>
            </h1>
          </div>
          <div className="flex flex-col-reverse md:flex-col items-center md:items-start text-left self-center mt-4 md:mt-0 w-full whitespace-nowrap">
            <div>
              {belowMd &&
                <BureauLogo isMobile lightTheme={useLightTheme} />
              }
              <div className={`py-2 text-lg md:text-xl w-1/2 ${useLightTheme ? "text-black" : "text-white"}`}>
                <CheckMark className="my-4" color="boost">
                  <span className="ml-3">
                    Up to <span className="font-bold"> $30,000 credit line</span>
                  </span>
                </CheckMark>
                <CheckMark className="my-4" color="boost">
                  <span className="ml-3">
                    <span className="font-bold">105+ </span>
                    <span>credit boost</span>
                  </span>
                </CheckMark>
                <CheckMark className="my-4" color="boost">
                  <span className="ml-3 relative">
                    No credit checks. <span className="font-bold">Ever.</span>
                  </span>
                </CheckMark>
              </div>
              <div className="self-center md:self-start">
                <div
                  className="mt-2 flex flex-col lg:flex-row gap-x-4 gap-y-10 box-border"
                  style={{ maxWidth: 507 }}
                >
                  <Email
                    buttonText="Boost my score"
                    handleSubmit={handleSubmit}
                    componentClassName="flex-col md:flex-row space-y-2 md:space-y-0"
                    inputClassName={`w-full rounded-md md:rounded-r-none ${useLightTheme ? "border-2 md:border-1 shadow-[0_2px_4px_rgba(0,0,0,0.3)]" : "border"} ${useLightTheme ? "border-boost-primary-500" : "border-neutral-500"} md:border-r-0 ${useLightTheme ? "placeholder-boost-primary-400 placeholder:font-semibold placeholder:text-lg" : "placeholder-gray-600"} text-black ${useLightTheme ? "bg-[#F4F1FB]" : "bg-white"}`}
                    buttonClassName={`bg-boost-button-purple text-white w-full md:w-auto rounded-md md:rounded-l-none ${useLightTheme ? "border-2 md:border-1 shadow-[0_2px_4px_rgba(0,0,0,0.3)]" : "border"} border-boost-primary-500 font-semibold`}
                    onFocus={() => { hidePromoPopupRef.current = true; }}
                    onBlur={() => { hidePromoPopupRef.current = false; }}
                  />
                </div>
                {error && (
                  <p className="text-sm text-error-200 text-left ml-2 mt-2">
                    {error}
                  </p>
                )}
                <div className="mt-8 text-base flex items-center text-left text-success-700 whitespace-pre-wrap w-full">
                  <img src={Guard3} alt="" className="inline md:mr-2 h-10 md:h-6" />
                  <span>
                    Trusted by <span className="font-bold">4 million+</span>{" "}
                    people building credit with TomoBoost
                  </span>
                </div>
                {!belowMd &&
                  <BureauLogo lightTheme={useLightTheme} />
                }
              </div>
              {modalState && (
                <SignupConfirmationModal
                  email={modalEmail}
                  closeModal={closeModal}
                  modalState={modalState}
                />
              )}
            </div>
          </div>
        </div>
        <div className="mx-auto text-center md:block px-5 relative">
          <div
            className="md:hidden absolute z-50 self-center"
            style={{
              top: "50%",
              left: "50%",
              transform: "translateY(-50%) translateX(-50%)",
            }}
          >
            <h1
              className="mx-2 text-center md:text-left font-bold whitespace-nowrap"
              style={{ fontSize: 40 }}
            >
              <span className={`${useLightTheme ? "text-black" : "text-white"}`}>Build Credit Fast</span>
              <span className="text-boost-primary-500">.</span>
            </h1>
          </div>
          <div className="opacity-50 md:opacity-100 z-0 mx-6 md:mx-0">
            <BoostBannerImage belowMd={belowMd} />
          </div>
        </div>
      </div>
      {/* TESTIMONIAL CARDS SECTION */}
      <div
        className={`mt-10 md:mt-24 flex flex-col justify-center items-center mx-auto ${useLightTheme ? "bg-white" : "bg-transparent"}`}
        style={{ maxWidth: 1300 }}
      >
        <div className={`font-bold text-2xl ${useLightTheme ? "text-neutral-600" : "text-neutral-500"}`}>Hear what others have to say</div>
        <div className="flex overflow-x-scroll hide-x-scrollbar shrink-0 w-full">
          <div className="mx-6 md:mx-10 mt-5 pb-10 flex self-start overflow-visible">
            {
              [(useLightTheme ? TestimonialCard1Light : TestimonialCard1), (useLightTheme ? TestimonialCard2Light : TestimonialCard2), (useLightTheme ? TestimonialCard3Light : TestimonialCard3), (useLightTheme ? TestimonialCard4Light : TestimonialCard4)]
                .map((imageSrc, idx) => (
                  <img
                    alt=""
                    src={imageSrc}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`testimonial-${idx}`}
                    style={{ width: 295, height: "auto" }}
                    className="pr-6 box-content transform hover:scale-110 transition-transform"
                  />
                ))
            }
          </div>
        </div>
      </div>
      {/* SOCIAL PROOF CAROUSEL */}
      <div className="py-8 bg-neutral-300">
        <AutoplayCarousel
          titleText="As mentioned in"
          duplicationFactor={3}
          totalWidthPreload={7752} // will need to change this if adding/removing image
          items={socialProofImages.map((item) => {
            return (
              <div className="" style={{ height: 30 }}>
                <img src={item.img} alt={item.alt} className="" />
              </div>
            );
          })}
        />
      </div>
      {/* Features Title */}
      <div className="w-full text-center">
        <p className={`font-bold text-xl md:text-2xl mt-6 mb-4 md:my-8 text-boost-primary-500`}>
          We have the right tools for you
        </p>
      </div>
      {/* FEATURES */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <ActionCard
          title="Credit Monitoring"
          subtitle="See Your Credit in Real-Time"
          paragraphOne="Get real-time credit score updates to stay on top of your financial health. Easily monitor your credit score, track your subscriptions, and manage your credit profile all in one convenient place."
          paragraphTwo="Whether you’re looking to boost your credit score fast, build credit history, or safeguard your progress towards a better line of credit, staying informed is the key to unlocking your credit success."
          ctaText="🌟 Get Started Today!"
          heroImage={useLightTheme ? DesktopImage1Light : DesktopImage1}
          heroImageMobile={useLightTheme ? MobileImage1Light :MobileImage1}
          imageClass="h-auto sm:h-3/4 md:h-4/5 mdl:h-5/6 lg:h-80"
          isMobile={belowMd}
          lightTheme={useLightTheme}
        />
        <ActionCard
          title="AI financial advisor"
          subtitle="Your Personal Guide to Smarter Financial Decisions"
          paragraphOne="Get instant, personalized answers with our AI financial advisor to help you make smarter, more confident financial choices. Whether you’re aiming to boost your credit score fast or secure loans with bad credit, explore the best credit building app with our AI-powered tools to provide tailored guidance for your unique credit repair needs."
          ctaText="🌟 Get Started Today!"
          heroImage={useLightTheme ? DesktopImage2Light : DesktopImage2}
          heroImageMobile={useLightTheme ? MobileImage2Light :MobileImage2}
          imageClass="h-auto sm:h-3/4 md:h-4/5 mdl:h-5/6 lg:h-80"
          isMobile={belowMd}
          lightTheme={useLightTheme}
          reversed
        />
        <ActionCard
          title="Fix damaging report mistakes"
          subtitle="Protect Your Credit Score"
          paragraphOne="Easily file disputes on credit report inaccuracies to safeguard your financial health. Removing errors like incorrect late payments, duplicate accounts, or outdated information can quickly boost your credit score and improve your creditworthiness."
          paragraphTwo="Whether you're working to remove mistakes, remove hard inquiries, or ensure a clean report, addressing these inaccuracies is a critical step.
Take control of your credit today and keep your score healthy for better loan offers, credit cards, and financial opportunities!"
          ctaText="🌟 Get Started Today!"
          heroImage={useLightTheme ? DesktopImage3Light : DesktopImage3}
          heroImageMobile={useLightTheme ? MobileImage3Light :MobileImage3}
          imageClass="h-auto sm:h-3/4 md:h-4/5 mdl:h-5/6 lg:h-80"
          isMobile={belowMd}
          lightTheme={useLightTheme}
        />
        <ActionCard
          title="Bill Tracking"
          subtitle="Stay Organized and Take Control of Your Finance"
          paragraphOne="Simplify your financial life with our bill tracking tool that helps you stay organized and on top of your expenses. Whether you're working to boost your credit score, manage your budget, or build financial confidence, effective bill tracking can play a key role in achieving your goals."
          paragraphTwo="Take the stress out of managing your finances and stay ahead with tools designed to help you succeed."
          ctaText="🌟 Get Started Today!"
          heroImage={useLightTheme ? DesktopImage4Light : DesktopImage4}
          heroImageMobile={useLightTheme ? MobileImage4Light :MobileImage4}
          imageClass="h-auto sm:h-3/4 md:h-4/5 mdl:h-5/6 lg:h-80"
          isMobile={belowMd}
          lightTheme={useLightTheme}
          reversed
        />
        <ActionCard
          title="Credit building loan"
          subtitle="Boost Your Credit Score and Expand Your Opportunities"
          paragraphOne="Discover how a credit building loan can help you raise your credit score fast and expand your credit file. Building your credit history opens doors to more loan offers, credit cards, and better financial opportunities."
          paragraphTwo="Whether you're looking to increase your credit score by 100 points overnight, establish a strong financial foundation, or access loans with poor credit, our credit booster loan will make it easier to achieve your goals."
          ctaText="🌟 Get Started Today!"
          heroImage={useLightTheme ? DesktopImage5Light : DesktopImage5}
          heroImageMobile={useLightTheme ? MobileImage5Light :MobileImage5}
          imageClass="h-auto sm:h-3/4 md:h-4/5 mdl:h-5/6 lg:h-80"
          isMobile={belowMd}
          lightTheme={useLightTheme}
          comingSoon
        />
      </Box>
    </div>
  );
};
